import { useEffect, useRef } from 'react';
import {
  Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, Tooltip, Typography,
} from '@mui/material';
import CloseIcon         from '@mui/icons-material/Close';
import SkipPreviousIcon  from '@mui/icons-material/SkipPrevious';
import SkipNextIcon      from '@mui/icons-material/SkipNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon  from '@mui/icons-material/NavigateNext';
import type { LiveGameEvent } from '../liveMatchTypes.ts';
import { useMatchReplay }    from './useMatchReplay.ts';
import ReplayScoreBoard      from './ReplayScoreBoard.tsx';
import VolleyballFieldView   from './VolleyballFieldView.tsx';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: number;
  homeTeamName?: string;
  awayTeamName?: string;
  homeShirtColor?: string;
  awayShirtColor?: string;
  liveMode?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EVENT_COLORS: Record<string, string> = {
  POINT_HOME:        '#4fc3f7',
  POINT_AWAY:        '#ef9a9a',
  TIMEOUT_HOME:      '#fff176',
  TIMEOUT_AWAY:      '#fff176',
  SUBSTITUTION_HOME: '#a5d6a7',
  SUBSTITUTION_AWAY: '#ce93d8',
};

const EVENT_ICONS: Record<string, string> = {
  POINT_HOME:        '●',
  POINT_AWAY:        '●',
  TIMEOUT_HOME:      'T',
  TIMEOUT_AWAY:      'T',
  SUBSTITUTION_HOME: '⇄',
  SUBSTITUTION_AWAY: '⇄',
};

function eventLabel(
  event: LiveGameEvent,
  homeTeamName: string,
  awayTeamName: string,
  homeCallupMap: Map<number, number>,
  awayCallupMap: Map<number, number>,
): string {
  const { type, homePoints, awayPoints, playerId, substituteId } = event;
  const score = `${homePoints}–${awayPoints}`;
  switch (type) {
    case 'POINT_HOME':        return `Punto ${homeTeamName}  ${score}`;
    case 'POINT_AWAY':        return `Punto ${awayTeamName}  ${score}`;
    case 'TIMEOUT_HOME':      return `Timeout ${homeTeamName}  ${score}`;
    case 'TIMEOUT_AWAY':      return `Timeout ${awayTeamName}  ${score}`;
    case 'SUBSTITUTION_HOME': {
      const out = playerId    ? `#${homeCallupMap.get(playerId)    ?? '?'}` : '?';
      const ins = substituteId ? `#${homeCallupMap.get(substituteId) ?? '?'}` : '?';
      return `Sostituzione ${homeTeamName}  ${out} → ${ins}`;
    }
    case 'SUBSTITUTION_AWAY': {
      const out = playerId    ? `#${awayCallupMap.get(playerId)    ?? '?'}` : '?';
      const ins = substituteId ? `#${awayCallupMap.get(substituteId) ?? '?'}` : '?';
      return `Sostituzione ${awayTeamName}  ${out} → ${ins}`;
    }
    default: return type;
  }
}

// ─── ReplayTimeline ──────────────────────────────────────────────────────────

interface TimelineProps {
  events: LiveGameEvent[];
  cursor: number;
  onJump: (index: number) => void;
  sets: number[];
  currentSet: number;
}

function ReplayTimeline({ events, cursor, onJump, sets, currentSet }: TimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef    = useRef<HTMLDivElement>(null);

  // Scorri automaticamente per tenere il cursore visibile
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [cursor]);

  // Filtra per set corrente se ci sono più set
  const visibleEvents = sets.length > 1
    ? events.filter((e) => e.setNumber === currentSet)
    : events;
  const offset = sets.length > 1
    ? events.findIndex((e) => e.setNumber === currentSet)
    : 0;

  return (
    <Box sx={{ bgcolor: 'rgba(0,0,0,0.35)', px: 1, py: 0.75 }}>
      {/* Tabs set */}
      {sets.length > 1 && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
          {sets.map((s) => (
            <Box
              key={s}
              onClick={() => {
                const idx = events.findIndex((e) => e.setNumber === s);
                onJump(idx >= 0 ? idx : 0);
              }}
              sx={{
                px: 1, py: 0.25, borderRadius: 1, cursor: 'pointer', fontSize: 11,
                fontWeight: s === currentSet ? 700 : 400,
                color: s === currentSet ? '#FFD700' : 'rgba(255,255,255,0.5)',
                bgcolor: s === currentSet ? 'rgba(255,215,0,0.12)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Set {s}
            </Box>
          ))}
        </Box>
      )}

      {/* Indicatori eventi */}
      <Box
        ref={containerRef}
        sx={{ display: 'flex', gap: '3px', overflowX: 'auto', pb: 0.5, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        {visibleEvents.map((evt, i) => {
          const globalIdx = offset + i;
          // cursor è 1-based rispetto agli eventi: cursor=k significa che events[k-1] è stato applicato
          const isApplied = globalIdx < cursor;
          const isCurrent = globalIdx === cursor - 1;
          const color     = EVENT_COLORS[evt.type] ?? 'rgba(255,255,255,0.5)';
          const icon      = EVENT_ICONS[evt.type]  ?? '•';
          return (
            <Tooltip key={globalIdx} title={`${evt.homePoints}–${evt.awayPoints}`} arrow placement="top">
              <Box
                ref={isCurrent ? activeRef : undefined}
                onClick={() => onJump(globalIdx + 1)}
                sx={{
                  width: 18, height: 18, borderRadius: evt.type.includes('POINT') ? '50%' : '3px',
                  bgcolor: isApplied ? color : 'rgba(255,255,255,0.12)',
                  border: isCurrent ? `2px solid ${color}` : '1px solid rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0,
                  fontSize: 8, color: isApplied ? '#000' : 'rgba(255,255,255,0.3)',
                  fontWeight: 700,
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: `${color}88` },
                }}
              >
                {icon}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── MatchReplayDialog ────────────────────────────────────────────────────────

export default function MatchReplayDialog({
  open, onClose, eventId,
  homeTeamName: homeTeamNameProp,
  awayTeamName: awayTeamNameProp,
  homeShirtColor: homeShirtColorProp,
  awayShirtColor: awayShirtColorProp,
  liveMode = false,
}: Props) {
  const replay = useMatchReplay(eventId, liveMode);

  const snap = replay.currentSnapshot;

  // Nomi e colori: props hanno precedenza, poi quelli dal backend (via callupDataJson), poi default
  const homeTeamName = homeTeamNameProp ?? replay.homeTeamName;
  const awayTeamName = awayTeamNameProp ?? replay.awayTeamName;
  const homeShirtColor = homeShirtColorProp ?? replay.homeShirtColor;
  const awayShirtColor = awayShirtColorProp ?? replay.awayShirtColor;

  // Se la nostra squadra ha giocato come AWAY, scambiamo le etichette per mostrare
  // sempre "homeTeamName" (noi) sul lato corretto del tabellone.
  const swappedNames = replay.ourTeamSide === 'AWAY';
  const displayHome = swappedNames ? awayTeamName : homeTeamName;
  const displayAway = swappedNames ? homeTeamName : awayTeamName;

  // Mappa playerId → shirtNumber per le etichette di sostituzione
  const homeShirtMap = new Map(replay.homeCallup.map((p) => [p.id, p.shirtNumber]));
  const awayShirtMap = new Map(replay.awayCallup.map((p) => [p.id, p.shirtNumber]));

  // Polling ogni 5s in liveMode
  useEffect(() => {
    if (!liveMode || !open) return;
    const id = setInterval(() => replay.reload(), 5000);
    return () => clearInterval(id);
  }, [liveMode, open, replay.reload]);

  // Navigazione da tastiera (solo in replay, non live)
  useEffect(() => {
    if (!open || liveMode) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') replay.goNext();
      if (e.key === 'ArrowLeft')  replay.goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, liveMode, replay.goNext, replay.goPrev]);

  const currentSetNumber = snap?.currentSet ?? replay.sets[0] ?? 1;
  const label = replay.currentEvent
    ? eventLabel(replay.currentEvent, displayHome, displayAway, homeShirtMap, awayShirtMap)
    : 'Inizio partita';

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ bgcolor: '#0d0d1a', color: 'white', py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        {liveMode && (
          <Chip label="LIVE" color="error" size="small" sx={{ fontWeight: 700, animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
        )}
        <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700 }}>
          {liveMode ? 'Live — ' : 'Replay — '}{displayHome} vs {displayAway}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#0d1117', p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {replay.loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        )}

        {replay.error && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="error">{replay.error}</Typography>
          </Box>
        )}

        {!replay.loading && !replay.error && snap && (
          <>
            {/* Tabellone */}
            <ReplayScoreBoard
              homeTeamName={displayHome}
              awayTeamName={displayAway}
              homePoints={snap.homePoints}
              awayPoints={snap.awayPoints}
              setsWon={snap.setsWon}
              completedSets={snap.completedSets}
              currentSet={snap.currentSet}
              servingTeam={snap.servingTeamNow}
              swapped={swappedNames}
            />

            {/* Campo */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <VolleyballFieldView
                homeLineup={snap.homeLineup}
                awayLineup={snap.awayLineup}
                homeCallup={replay.homeCallup}
                awayCallup={replay.awayCallup}
                homeShirtColor={homeShirtColor}
                awayShirtColor={awayShirtColor}
                homeLiberoId={replay.homeLiberoId}
                awayLiberoId={replay.awayLiberoId}
                homeTeamName={displayHome}
                awayTeamName={displayAway}
                swapped={swappedNames}
              />
            </Box>

            {/* Etichetta evento */}
            <Box sx={{ px: 2, py: 0.75, bgcolor: 'rgba(0,0,0,0.5)', textAlign: 'center' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: { xs: '0.8rem', sm: '0.95rem' }, fontWeight: 500 }}>
                {label}
              </Typography>
            </Box>

            {/* Timeline */}
            <ReplayTimeline
              events={replay.events}
              cursor={replay.cursor}
              onJump={replay.jumpTo}
              sets={replay.sets}
              currentSet={currentSetNumber}
            />

            {/* Barra di navigazione (solo in replay, non in live) */}
            {!liveMode && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, bgcolor: '#0d0d1a', flexShrink: 0 }}>
              <Tooltip title="Inizio partita">
                <span>
                  <IconButton onClick={replay.jumpToStart} disabled={replay.cursor === 0} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    <SkipPreviousIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Evento precedente  ←">
                <span>
                  <IconButton onClick={replay.goPrev} disabled={replay.cursor === 0} sx={{ color: 'white' }} size="large">
                    <NavigateBeforeIcon fontSize="large" />
                  </IconButton>
                </span>
              </Tooltip>

              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, minWidth: 60, textAlign: 'center' }}>
                {replay.cursor} / {replay.total}
              </Typography>

              <Tooltip title="Evento successivo  →">
                <span>
                  <IconButton onClick={replay.goNext} disabled={replay.cursor === replay.total} sx={{ color: 'white' }} size="large">
                    <NavigateNextIcon fontSize="large" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Fine partita">
                <span>
                  <IconButton onClick={replay.jumpToEnd} disabled={replay.cursor === replay.total} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    <SkipNextIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
